import { useEffect, useState } from 'react'
import { dataLayerPush } from '../../../helpers/thirdparty'
import themeConfig from '../../../../theme.config'
import * as HeroIcons from '@heroicons/react/24/solid'

const primaryButtonClasses =
  'bg-(--color-button-primary-bg) text-(--color-button-primary-text) border-(--color-button-primary-border) hover:bg-(--color-button-primary-hover-bg) hover:text-(--color-button-primary-hover-text) hover:border-(--color-button-primary-hover-border) active:bg-(--color-button-primary-active-bg) active:text-(--color-button-primary-active-text) active:border-(--color-button-primary-active-border) border-2'
const secondaryButtonClasses =
  'bg-(--color-button-secondary-bg) text-(--color-button-secondary-text) border-(--color-button-secondary-border) hover:bg-(--color-button-secondary-hover-bg) hover:text-(--color-button-secondary-hover-text) hover:border-(--color-button-secondary-hover-border) active:bg-(--color-button-secondary-active-bg) active:text-(--color-button-secondary-active-text) active:border-(--color-button-secondary-active-border) border-2'

// no text overrides required for text button, now inherits from section
const textButtonClasses = ''

const getSectionTitleText = (titleEl) => {
  if (!titleEl) return ''
  const div = document.createElement('div')
  div.innerHTML = titleEl.innerHTML
  div.querySelector('.text-warm-plum')?.remove()
  return div.innerText.trim()
}

// Walk up from `el` through ancestor <section> elements (up to <main>) looking for
// [data-type="section-title"].
const getNearestHeading = (el) => {
  const main = el.closest('main')
  let section = el.closest('section')

  while (section && (!main || main.contains(section))) {
    const title = getSectionTitleText(section.querySelector('[data-type="section-title"]'))
    if (title) return title

    let parent = section.parentElement
    section = null
    while (parent) {
      if (main && parent === main) break
      if (parent.tagName === 'SECTION') {
        section = parent
        break
      }
      parent = parent.parentElement
    }
  }

  return ''
}

const DynamicHeroIcon = (props) => {
  const { ...icons } = HeroIcons
  let TheIcon
  if (props.link?.linkIconClass) {
    TheIcon = icons[`${props.link?.linkIconClass}Icon`]
  } else if (props.icon) {
    TheIcon = icons[`${props.icon}Icon`]
  }

  let iconPositionClass = 'mr-2'
  if (!props.before) {
    iconPositionClass = 'ml-2'
  }

  return (
    <TheIcon
      className={`h-6 ${iconPositionClass} shrink-0 w-6 text-current ${props.iconClass ? props.iconClass : ''}`}
      role="none"
      aria-hidden="true"
    />
  )
}

function ButtonEl(props) {
  const type = ['default', 'rounded', 'circle'].includes(props.buttonType) ? props.buttonType : 'default'
  let theme = ['primary', 'secondary', 'text', 'none'].includes(props.theme) ? props.theme : 'secondary'
  theme = theme === 'text' ? 'none' : theme

  const [aria, setAria] = useState({})

  let typeClasses = type === 'default' ? 'rounded' : 'rounded-full'
  // if the config is set to not use square buttons, override the class
  themeConfig.settings.useOnlyPillButtons && (typeClasses = 'rounded-full')

  useEffect(() => {
    setAria((prevState) => {
      let newState = { ...prevState }
      if (!props.ariaHidden || props.ariaHidden === 'false') {
        delete newState['aria-hidden']
        delete newState.tabIndex
      } else {
        newState['aria-hidden'] = 'true'
        newState.tabIndex = -1
      }
      if (props.ariaHasPopup) {
        newState['aria-haspopup'] = props.ariaHasPopup
      }
      if (props.ariaControls) {
        newState['aria-controls'] = props.ariaControls
      } else {
        delete newState['aria-controls']
      }
      if (props.ariaExpanded) {
        newState['aria-expanded'] = props.ariaExpanded
      } else {
        delete newState['aria-expanded']
      }
      if (props.ariaPressed !== undefined) {
        newState['aria-pressed'] = props.ariaPressed
      } else {
        delete newState['aria-pressed']
      }
      return newState
    })
  }, [props.ariaHidden, props.ariaControls, props.ariaExpanded, props.ariaPressed])

  const className = `cta group rounded-md font-semibold ${theme !== 'none' ? 'px-8' : ''} py-2.5 focus-visible:outline-2 focus-visible:outline-offset-2 flex transition-all duration-300 ${typeClasses} ${props.className ? props.className : ''} ${props.theme === 'primary' || !props.theme ? primaryButtonClasses : props.theme === 'secondary' ? secondaryButtonClasses : textButtonClasses}`

  const inner = (
    <div className="flex flex-wrap items-center w-full">
      {/* {theme !== 'none' && ( */}
      <>
        <div className="cta-content flex items-center justify-center wrap-anywhere w-full">
          {props.icon && props.position === 'Before' && <DynamicHeroIcon {...props} before={true} />}
          {props.label || props.children || props.link?.name || props.link?.label}
          {props.icon && props.position === 'After' && <DynamicHeroIcon {...props} before={false} />}
        </div>
        {props.showDecorator !== false && (
          <span
            data-type="button-decorator"
            className={`block basis-full mx-auto max-w-px bg-transparent group-hover:max-w-full transition-all duration-500 h-0.5 group-hover:bg-[currentColor]`}
          />
        )}
      </>
    </div>
  )

  const handleClick = (e) => {
    if (!props.skipCtaClickTracking) {
      dataLayerPush({
        event: `${props.theme || 'primary'}_cta_click`,
        click_text: props.label || props.children || props.link?.name || props.link?.label,
        destination_path: props.link?.url,
        page_section: getNearestHeading(e.currentTarget),
      })
    }
    props.onClick?.(e)
  }

  return props.link ? (
    <a
      href={props.link.url}
      target={props.link.target}
      // target="_blank" links get rel="noopener" by default so downloads/navigation escape the
      // sandboxed Umbraco preview iframe safely; an explicit link.rel always wins.
      rel={props.link.rel ?? (props.link.target === '_blank' ? 'noopener' : undefined)}
      download={props.link.download}
      className={`${className} cursor-pointer`}
      tabIndex={props.active === undefined || props.active === 'true' ? 0 : -1}
      {...aria}
      data-idx={props.idx || '0'}
      data-style={props.theme}
      data-bg={props.dataBg}
      style={props.style}
      onClick={handleClick}
    >
      {inner}
    </a>
  ) : (
    <button
      type={props.type ?? 'button'}
      className={`${className} cursor-pointer`}
      onClick={(e) => {
        handleClick(e)
      }}
      disabled={props.disabled}
      {...aria}
      data-idx={props.idx || '0'}
      data-style={props.theme}
      data-bg={props.dataBg}
      style={props.style}
    >
      {inner}
    </button>
  )
}

export default ButtonEl
